import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { campaign } from './campaign';
import { SmsConsentDisclosure } from './sms-disclosure';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  message: '',
};

type ContactForm = typeof initialForm;

function ContactPage() {
  const [form, setForm] = useState<ContactForm>(initialForm);
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    setForm((current) => {
      switch (name) {
        case 'name': {
          return { ...current, name: value };
        }
        case 'email': {
          return { ...current, email: value };
        }
        case 'phone': {
          return { ...current, phone: value };
        }
        case 'message': {
          return { ...current, message: value };
        }
        default: {
          return current;
        }
      }
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!termsConsent) {
      alert('Please agree to the Terms and Conditions and Privacy Policy to continue.');
      return;
    }

    setStatus('submitting');
    try {
      const data = new URLSearchParams();
      for (const [key, value] of Object.entries(form)) {
        data.append(key, value);
      }
      data.append('campaign', campaign.committee);
      data.append('source', 'contact-page');
      data.append('phoneConsent', phoneConsent.toString());
      data.append('termsConsent', termsConsent.toString());

      const response = await fetch(campaign.contactEndpoint, {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
      });

      if (!response.ok) {
        throw new Error('Submission failed.');
      }

      setForm(initialForm);
      setPhoneConsent(false);
      setTermsConsent(false);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <main className="bg-white px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="font-black uppercase tracking-[0.2em] text-[#8f6400]">Contact</p>
          <h1
            className="mt-3 text-5xl font-black text-[#6f2182]"
            style={{ fontFamily: 'Source Serif 4, serif' }}
          >
            Reach Beau&apos;s campaign
          </h1>
          <p className="mt-5 text-lg leading-8 text-[#66546f]">
            Ask a question, request a yard sign, offer to volunteer, or invite Beau to a District 5
            neighborhood conversation.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="rounded-[2rem] bg-[#f8f1fb] p-8 shadow-xl">
            {status === 'success' ? (
              <div className="rounded-2xl bg-white p-8 text-center shadow-lg">
                <h2 className="text-3xl font-black text-[#6f2182]">Message sent</h2>
                <p className="mt-4 text-[#66546f]">
                  Thanks for reaching out. The campaign will be in touch shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-7 rounded-full bg-[#6f2182] px-7 py-3 text-sm font-black uppercase tracking-[0.16em] text-white transition hover:bg-[#551567]"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <Field label="Name" name="name" value={form.name} onChange={handleChange} />
                <Field
                  label="Email*"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  required
                />
                <Field
                  label="Phone (optional)"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="tel"
                  placeholder="Phone (optional for SMS updates)"
                />
                <div>
                  <label htmlFor="message" className="mb-2 block font-bold text-[#6f2182]">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-[#dbc4e4] bg-white p-4 outline-none ring-[#f2b800] transition focus:ring-2"
                  />
                </div>

                <div className="flex gap-3 rounded-2xl bg-white p-4 text-sm leading-6 text-[#66546f]">
                  <input
                    id="contact-phone-consent"
                    aria-label="Optional SMS opt-in consent"
                    type="checkbox"
                    checked={phoneConsent}
                    onChange={() => setPhoneConsent((current) => !current)}
                    className="mt-1 size-5 rounded border-[#6f2182] text-[#6f2182]"
                  />
                  <SmsConsentDisclosure />
                </div>

                <label
                  htmlFor="contact-terms-consent"
                  className="flex gap-3 rounded-2xl bg-white p-4 text-sm leading-6 text-[#66546f]"
                >
                  <input
                    id="contact-terms-consent"
                    type="checkbox"
                    checked={termsConsent}
                    onChange={() => setTermsConsent((current) => !current)}
                    required
                    className="mt-1 size-5 rounded border-[#6f2182] text-[#6f2182]"
                  />
                  <span>
                    I agree to the{' '}
                    <Link to="/terms-and-conditions" className="font-bold text-[#6f2182] underline">
                      Terms and Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy-policy" className="font-bold text-[#6f2182] underline">
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={status === 'submitting' || !termsConsent}
                  className={`rounded-full px-8 py-4 text-sm font-black uppercase tracking-[0.16em] shadow-lg transition ${status === 'submitting' || !termsConsent ? 'bg-gray-300 text-gray-500' : 'bg-[#f2b800] text-[#431052] hover:bg-white'}`}
                >
                  {status === 'submitting' ? 'Sending...' : 'Send message'}
                </button>
                {status === 'error' && (
                  <p className="font-semibold text-[#8f6400]">
                    Something went wrong. Please email {campaign.email}.
                  </p>
                )}
              </form>
            )}
          </section>

          <aside className="rounded-[2rem] bg-[#6f2182] p-8 text-white shadow-xl">
            <h2 className="text-3xl font-black" style={{ fontFamily: 'Source Serif 4, serif' }}>
              Campaign contact
            </h2>
            <div className="text-white/82 mt-7 space-y-5">
              <p>
                <span className="block text-sm font-black uppercase tracking-[0.16em] text-[#f5c400]">
                  Campaign email
                </span>
                <a
                  href={`mailto:${campaign.email}`}
                  className="text-xl font-bold text-white hover:text-[#f5c400]"
                >
                  {campaign.email}
                </a>
              </p>
              <p>
                <span className="block text-sm font-black uppercase tracking-[0.16em] text-[#f5c400]">
                  Official city profile
                </span>
                <a
                  href={campaign.cityProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-white hover:text-[#f5c400]"
                >
                  View Beau&apos;s Galveston profile
                </a>
              </p>
              <p>
                <span className="block text-sm font-black uppercase tracking-[0.16em] text-[#f5c400]">
                  Facebook
                </span>
                <a
                  href={campaign.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-white hover:text-[#f5c400]"
                >
                  Campaign page
                </a>
              </p>
            </div>
            <div className="mt-10 rounded-2xl bg-white/10 p-6">
              <h3 className="text-xl font-black">Need voting help?</h3>
              <p className="mt-3 leading-7 text-white/75">
                Use Galveston Votes for official registration, precinct, and voting location
                information.
              </p>
              <a
                href={campaign.galvestonVotesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-block rounded-full bg-[#f5c400] px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-[#6f2182] transition hover:bg-white"
              >
                Galveston Votes
              </a>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

type FieldProps = {
  label: string;
  name: keyof ContactForm;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
};

function Field({ label, name, value, onChange, type = 'text', placeholder, required }: FieldProps) {
  return (
    <div>
      <label htmlFor={name} className="mb-2 block font-bold text-[#6f2182]">
        {label}
      </label>
      <input
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-xl border border-[#dbc4e4] bg-white p-4 outline-none ring-[#f2b800] transition focus:ring-2"
      />
    </div>
  );
}

export default ContactPage;
